import axios from 'axios';
import { UNAUTHORIZED, FORBIDDEN } from 'http-status';
import omit from 'lodash/omit';
import store from './store'; // eslint-disable-line import/no-cycle

const {
  VUE_APP_API_PREFIX: apiPrefix,
  VUE_APP_HANDLE: appHandle,
  VUE_APP_AUTH_TOKEN: appAuthToken,
  VUE_APP_APP_NAME: appName,
  VUE_APP_APP_VERSION: appVersion,
  VUE_APP_BUILD_NUMBER: appBuild
} = process.env;

const KeyboardingAPI = axios.create();

const POST = 'post';
const GET = 'get';

KeyboardingAPI.interceptors.request.use(
  // eslint-disable-next-line arrow-parens
  config => {
    const {
      student: { sessionId }
    } = store.get();

    if (sessionId) {
      // eslint-disable-next-line no-param-reassign
      config.headers['x-lwtears-session'] = sessionId;
    } else {
      // eslint-disable-next-line no-param-reassign
      config.headers['x-ssousername'] = appHandle;
      // eslint-disable-next-line no-param-reassign
      config.headers['x-authtoken'] = decodeURIComponent(appAuthToken);
    }

    return config;
  },
  error => Promise.reject(error)
);

KeyboardingAPI.defaults.baseURL = apiPrefix;
KeyboardingAPI.defaults.headers.common['x-lwtears-app'] = appName;
KeyboardingAPI.defaults.headers.common['x-lwtears-app-version'] = appVersion;
KeyboardingAPI.defaults.headers.common['x-lwtears-app-build'] = appBuild || 'dev';

const createLogEvent = (type, data, options) => {
  const timestamp = new Date().toISOString();
  try {
    KeyboardingAPI.post('/logs', { type, timestamp, data }, options);
  } catch (err) {}
};

const createEvent = (type, data) => {
  const timestamp = new Date().toISOString();
  try {
    KeyboardingAPI.post('/events', { type, timestamp, data });
  } catch (err) {}
};

const execute = async (method, url, { headers = {}, ...options } = {}) => {
  try {
    const { status, data } = await KeyboardingAPI({
      method,
      url,
      headers,
      ...options
    });
    return {
      status,
      data
    };
  } catch (error) {
    const { response: { status, data } = {} } = error || {};
    if ([UNAUTHORIZED, FORBIDDEN].includes(status)) return { status };

    const { data: body, ...opts } = options;
    createLogEvent('api-error', { method, url, body, ...opts, status, data }, { headers });
    return { status };
  }
};

export default {
  async addProgressData(activityType, sessionId, body) {
    const data = {
      ...omit(body, ['grade', 'productId', 'asn', 'week', 'speedyKid'])
    };

    const result = await execute(POST, '/students/me/progress', { data });

    const { status } = result;

    if (status < 400) {
      const { errorData } = body;
      const errorCount = errorData ? errorData.length : 0;
      createEvent('activityCompleted', {
        ...omit(body, ['errorData', 'responseData']),
        errorCount,
        activityType
      });
    }

    return result;
  },

  async addScheduledActivityData(activityType, sessionId, scheduledActivityId, body) {
    const data = {
      ...omit(body, ['grade', 'productId', 'asn', 'week', 'speedyKid', 'activityId'])
    };
    const result = await execute(POST, `/students/me/scheduled-activities/${scheduledActivityId}`, {
      data
    });
    const { status } = result;
    if (status < 400) {
      const { errorData } = body;
      const errorCount = errorData ? errorData.length : 0;
      createEvent('assessmentCompleted', {
        activityId: scheduledActivityId,
        ...omit(body, ['errorData', 'responseData']),
        errorCount,
        activityType
      });
    }
    return result;
  },

  async getCurriculum() {
    return execute(GET, '/students/me/curriculum');
  },

  createEvent,
  createLogEvent
};
